import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import PartnerGymFollowRow from './PartnerGymFollowRow';
import PartnerGymActionPopover from './PartnerGymActionPopover';
import CenterFollowModal from '../../Model/Individual/MySettingModel/CenterFollowModal';
import PartnerGymInfoModal from '../../Model/Individual/MySettingModel/PartnerGymInfoModal';
import PartnerGymDisconnectModal from '../../Model/Individual/MySettingModel/PartnerGymDisconnectModal';

export default function PartnerGymFollowBox(props) {
  const { follows, followers, onClickFollow, onClickFollowerReject, onClickPermissionSave } = props;

  const { t } = useLanguage();

  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [followStoreIds, setFollowStoreIds] = useState([]);
  const [clickData, setClickData] = useState(undefined);
  const [isFollow, setIsFollow] = useState(true);
  const [isClickedFollowSearch, setIsClickedFollowSearch] = useState(false);

  const [infoModal, setInfoModal] = useState(undefined);
  const [popoverStoreId, setPopoverStoreId] = useState(undefined);
  const [disconnectModal, setDisconnectModal] = useState(undefined);

  // follows + followers 통합 리스트 — storeId 기준 유니크 (follows 우선) //
  const partners = useMemo(() => {
    const followRows = follows.map((data) => ({ data, isFollow: true }));
    const followerRows = followers.map((data) => ({ data, isFollow: false }));

    return [...new Map([...followerRows, ...followRows].map(row => [row.data.storeId, row])).values()];
  }, [follows, followers])

  // 팔로우 중인 업체 키 추출 //
  useEffect(() => {
    const storeIds = [];

    for (const data of follows) {
      storeIds.push(data.storeId);
    }

    setFollowStoreIds(storeIds);
  }, [follows])

  // 행 클릭 시 상세 모달 오픈 //
  function onClickRow(row) {
    // 상세보기(CenterFollowModal) 노출 비활성화 — 추후 복구 가능 //
    // setClickData(row.data);
    // setIsFollow(row.isFollow);
    // setIsClickedFollowSearch(true);
  }

  // 파트너 추가하기 버튼 클릭 시 //
  function onClickAddPartner() {
    setClickData(undefined);
    setIsFollow(true);
    setIsClickedFollowSearch(true);
  }

  // 모달에서 신청 / 승인 / 취소 클릭 시 //
  function onClickFollowButton(data) {
    // 신규 신청 케이스(상대와 관계 없음) → 권한 선택 모달 노출 //
    if (isFollow && data.status === undefined) {
      setInfoModal({ mode: 'application', data, isFollow: true });
      setIsClickedFollowSearch(false);
      return;
    }

    onClickFollow(data, isFollow);
  }

  // 행의 더보기 클릭 — 상태에 따라 모달/팝오버 분기 //
  function onClickMoreButton(rowIsFollow, rowData) {
    setPopoverStoreId(undefined);

    if (rowData.status === true) {
      setPopoverStoreId(rowData.storeId);
    }
    else if (rowIsFollow) {
      setInfoModal({ mode: 'pending-mine', data: rowData, isFollow: true });
    }
    else {
      setInfoModal({ mode: 'pending-theirs', data: rowData, isFollow: false });
    }
  }

  // 팝오버에서 권한 수정 클릭 //
  function onClickEditPermission(rowIsFollow, rowData) {
    setPopoverStoreId(undefined);

    // followers row 는 상대 소유 row → 내가 수정 불가 (백엔드에서 401) //
    if (!rowIsFollow) {
      alert(t('myStoreSetting.alert.permissionReadOnly'));
      return;
    }

    setInfoModal({ mode: 'edit-permission', data: rowData, isFollow: true });
  }

  // 팝오버에서 연결 종료 클릭 //
  function onClickEndConnection(rowIsFollow, rowData) {
    setPopoverStoreId(undefined);
    setDisconnectModal({ data: rowData, isFollow: rowIsFollow });
  }

  // 모달 좌측 버튼 클릭 (신청 취소 / 거절) //
  function onClickInfoCancel() {
    if (!infoModal) return;

    if (infoModal.mode === 'pending-mine') {
      onClickFollow(infoModal.data, true);
    }
    else if (infoModal.mode === 'pending-theirs') {
      if (onClickFollowerReject) onClickFollowerReject(infoModal.data);
    }

    setInfoModal(undefined);
  }

  // 모달 우측 버튼 클릭 (수락하기 / 저장하기 / 신청하기) //
  function onClickInfoSubmit(permissions) {
    if (!infoModal) return;

    if (infoModal.mode === 'pending-theirs') {
      onClickFollow(infoModal.data, false, permissions);
    }
    else if (infoModal.mode === 'edit-permission') {
      if (onClickPermissionSave) onClickPermissionSave(infoModal.data, permissions);
    }
    else if (infoModal.mode === 'application') {
      onClickFollow(infoModal.data, true, permissions);
    }

    setInfoModal(undefined);
  }

  // 연결 종료 확정 //
  function onClickConfirmDisconnect() {
    if (!disconnectModal) return;

    onClickFollow(disconnectModal.data, disconnectModal.isFollow);
    setDisconnectModal(undefined);
  }

  // 팔로워 행 렌더 (팝오버 포함) //
  function renderPartnerRow(row, index) {
    const isPopoverOpen = popoverStoreId === row.data.storeId;
    const popoverContent =
      isPopoverOpen ?
        <PartnerGymActionPopover
          onClickEditPermission={() => onClickEditPermission(row.isFollow, row.data)}
          onClickEndConnection={() => onClickEndConnection(row.isFollow, row.data)}
          onClose={() => setPopoverStoreId(undefined)} />
        :
        null;

    return (
      <PartnerGymFollowRow
        key={'PartnerGymFollowRow-' + index}
        data={row.data}
        isFollow={row.isFollow}
        onClickRow={() => onClickRow(row)}
        onClickMore={(rowData) => onClickMoreButton(row.isFollow, rowData)}
        popoverContent={popoverContent} />
    );
  }

  return (
    <TopLevelWrapper>
      {
        isClickedFollowSearch &&
        <CenterFollowModal
          isExercise={false}
          isFollow={isFollow}
          followStoreIds={followStoreIds}
          follows={follows}
          followers={followers}
          initialClickData={clickData}
          onClickFollow={onClickFollowButton}
          close={() => setIsClickedFollowSearch(false)} />
      }
      {
        infoModal &&
        <PartnerGymInfoModal
          mode={infoModal.mode}
          data={infoModal.data}
          isFollow={infoModal.isFollow}
          onClose={() => setInfoModal(undefined)}
          onCancel={onClickInfoCancel}
          onSubmit={onClickInfoSubmit} />
      }
      {
        disconnectModal &&
        <PartnerGymDisconnectModal
          data={disconnectModal.data}
          onCancel={() => setDisconnectModal(undefined)}
          onConfirm={onClickConfirmDisconnect} />
      }
      <SectionHeader>
        <SectionLabelText color={theme.primary}>{t('myStoreSetting.partnerGym.sectionLabel')}</SectionLabelText>
        <SectionTitleText color={theme.text}>{t('myStoreSetting.partnerGym.title')}</SectionTitleText>
      </SectionHeader>
      <ContentBox backgroundColor={theme.subBackground}>
        <ColumnHeader>
          <PartnerColumn>
            <ColumnHeaderText color={theme.text}>{t('myStoreSetting.partnerGym.columnPartner')}</ColumnHeaderText>
          </PartnerColumn>
          <FlexColumn>
            <ColumnHeaderText color={theme.text}>{t('myStoreSetting.partnerGym.columnStatus')}</ColumnHeaderText>
          </FlexColumn>
          <FlexColumn>
            <ColumnHeaderText color={theme.text}>{t('myStoreSetting.partnerGym.columnPermission')}</ColumnHeaderText>
          </FlexColumn>
          <MoreSpacer />
        </ColumnHeader>
        <Divider backgroundColor={theme.line} />
        <ListWrapper>
          {
            partners.length === 0 &&
            <EmptyBox>
              <EmptyText color={theme.subText}>{t('myStoreSetting.partnerGym.noPartner')}</EmptyText>
            </EmptyBox>
          }
          {
            partners.length > 0 &&
            partners.map((row, index) => renderPartnerRow(row, index))
          }
        </ListWrapper>
        <AddButton
          backgroundColor={theme.subBackground}
          borderColor={theme.primary}
          onClick={onClickAddPartner}>
          <AddButtonText color={theme.primary}>+</AddButtonText>
          <AddButtonText color={theme.primary}>{t('myStoreSetting.partnerGym.addPartner')}</AddButtonText>
        </AddButton>
      </ContentBox>
    </TopLevelWrapper>
  );
}

const TopLevelWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: stretch;

  margin-top: 36px;

  gap: 15px;

  width: 100%;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: flex-start;
  align-items: center;

  gap: 10px;

  width: 100%;
`;

const SectionLabelText = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  margin: 0;

  color: ${props => props.color};

  transition: all 0.3s ease-in-out;
`;

const SectionTitleText = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  margin: 0;

  color: ${props => props.color};

  transition: all 0.3s ease-in-out;
`;

const ContentBox = styled.div`
  display: flex;
  flex-direction: column;

  align-items: center;

  gap: 10px;

  padding: 20px;

  width: calc(100% - 40px);

  border-radius: 10px;

  background-color: ${props => props.backgroundColor};
`;

const ColumnHeader = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  gap: 10px;

  width: 100%;
  height: 30px;
`;

const PartnerColumn = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  flex-shrink: 0;

  width: 530px;
`;

const FlexColumn = styled.div`
  display: flex;
  flex-direction: row;

  align-items: center;

  flex: 1 0 0;
  min-width: 0;
`;

const MoreSpacer = styled.div`
  flex-shrink: 0;

  width: 50px;
  height: 30px;
`;

const ColumnHeaderText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};

  transition: all 0.3s ease-in-out;
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;

  background-color: ${props => props.backgroundColor};

  opacity: 0.5;
`;

const ListWrapper = styled.div`
  display: flex;
  flex-direction: column;

  align-items: stretch;

  width: 100%;
`;

const EmptyBox = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  width: 100%;
  height: 75px;
`;

const EmptyText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};
`;

const AddButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: center;

  gap: 10px;

  margin-top: 10px;

  width: 100%;
  height: 62px;

  border: 1.5px dashed ${props => props.borderColor};
  border-radius: 10px;

  background-color: ${props => props.backgroundColor};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const AddButtonText = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 14px;

  margin: 0;

  color: ${props => props.color};

  transition: all 0.3s ease-in-out;
`;
